import { useParams, useSearchParams } from "react-router-dom";
import Cookies from "js-cookie";
import { useQueries, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { v4 } from "uuid";

import { Separator } from "@/components/ui/separator/separator";
import { DateRangePicker } from "./_components/date-range-picker/date-range-picker2";
import { convertUTCtoIST } from "@/utils";
import { useDateRangeStore } from "./_components/date-range-picker/use-date-range-store";
import {
  getAllTodosQueryOptions,
  getSingleEmployeeQueryOptions,
} from "@/config/queries/query-options";
import { getStatusOfTodoForEmployee } from "@/config/queries/employee/emp-todo-queries";
import { ColumnType } from "./_components/board/types";
import { Board } from "./_components/board/board";

const SingleEmployeeTodosProgressPage = () => {
  const key = Cookies.get("key") as string;
  const [searchParams] = useSearchParams();
  const { empId } = useParams() as {
    empId: string;
  };

  const { data: SingleEmployeeData } = useQuery(
    getSingleEmployeeQueryOptions(key, empId)
  );

  const empName = searchParams.get("n");

  const { dateRange } = useDateRangeStore();

  const startdate = convertUTCtoIST(
    new Date(new Date(dateRange?.from as Date).setHours(0))
  );
  const enddate = convertUTCtoIST(
    new Date(new Date(dateRange?.to as Date).setHours(23))
  );

  const { data, isPending, isFetching } = useQuery(
    getAllTodosQueryOptions(key, startdate, enddate, empId)
  );

  const todosData = useMemo(() => {
    if (isPending) {
      return [];
    }

    if (data === undefined || data?.todos === undefined) {
      return [];
    }

    const result = data?.todos?.map((t) => {
      return {
        id: t?._id,
        title: t?.title,
        description: t?.description,
        recipients: t?.recipients,
        createdAt: t?.createdAt,
        deadline: t?.deadline,
        files: t?.files,
        tasks: t?.tasks,
      };
    });

    const reviewed = result?.filter((todo) => {
      const doesInclude = todo?.recipients?.find((r) => r?._id === empId);

      return doesInclude;
    });

    return reviewed?.sort(
      (a, b) =>
        new Date(b?.createdAt).getTime() - new Date(a?.createdAt).getTime()
    );
  }, [data, isPending, isFetching]);

  const queries = useQueries({
    queries: todosData
      ? todosData?.map((todo) => {
          return {
            queryKey: ["employee-todo-status", todo?.id],
            queryFn: () => getStatusOfTodoForEmployee(key)(todo?.id),
          };
        })
      : [],
  });

  const refineData = useMemo(() => {
    const resultt = queries?.map((query, index) => {
      const qData = (
        query?.data as {
          success: boolean;
          foundstatus: {
            status: string;
            employee: {
              _id: string;
            };
          }[];
        }
      )?.foundstatus;

      return {
        ...todosData[index],
        column: (qData?.length > 0
          ? qData?.find((q) => q?.employee?._id === empId)?.status || "Pending"
          : "Pending") as ColumnType,
      };
    });

    return resultt;
  }, [queries, data, isPending, isFetching]);

  return (
    <div className="p-3 h-[calc(100%-56px)]">
      <div className="flex items-center justify-between">
        <h3 className="text-2xl font-medium">
          Todos Progress of {empName || SingleEmployeeData?.employee?.name}
        </h3>

        <div className="flex items-center gap-2">
          <DateRangePicker />
        </div>
      </div>

      <Separator className="my-3" />

      <Board
        cardsData={refineData}
        key={`${refineData?.[0]?.id}-${v4()}-${empId}`}
      />
    </div>
  );
};

export default SingleEmployeeTodosProgressPage;
